import { Button, Chip, Divider, Icon, Theme, Typography, useTheme } from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from "react";
import clsx from 'clsx';
import { ArrowTooltip } from ".";
import { Scrollbars } from 'react-custom-scrollbars';
import { useTranslation } from "react-i18next";
import { nanoid } from "@reduxjs/toolkit";
import { DividerProps } from "@mui/material/Divider";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    verticalDivider: {
      padding: theme.spacing(0.5, 0.25),
      display: "flex",
      alignItems: "center"
    },
    icons: {
      '& > .fa': {
        margin: theme.spacing(2),
      },
      display: "flex",
      padding: theme.spacing(0.5),
    },
    iconButton: {
      minWidth: "0px !important",
      flex: 1
    },
    iconSize: {
      fontSize: "1.0em",
      width: "auto"
    },
    success: {
      color: theme.palette.success.main
    },
    error: {
      color: theme.palette.error.main
    },
    disabled: {
      opacity: 0.3
    },
    listIcon: {
      color: "rgba(0, 0, 0, 0.54)",
      fontSize: "20px"
    }
  })
);

interface DenseDividerProps extends DividerProps {
  spacing?: number[] | number;
}

export const DenseDivider = (props: DenseDividerProps) => {
  const {spacing, ...other} = props;

  let spacingDown;
  let spacingUp;

  if (typeof(spacing) == "number") {
    spacingDown = spacing;
    spacingUp = spacing;
  } else if (typeof(spacing) == "object") {
    spacingUp = spacing[0];
    spacingDown = spacing[1];
  } else {
    spacingUp = 20;
    spacingDown = 20;
  }

  return (
    <Divider {...other} style={{
      marginTop: `${spacingUp}px`,
      marginBottom: `${spacingDown}px`
    }}/>
  );
};

interface DividerWithTextProps {
  label: string;
  paddingTop?: number;
  paddingBottom?: number;
}

/**
 * Creates a divider with a chip element in the middle. Something
 * similar exists in the new material ui (5.x and up), but not in
 * our version (4.x), so we make our own instead.
 */
export const DividerWithText = (props: DividerWithTextProps) => {
  const theme = useTheme();

  const {label, paddingTop=32, paddingBottom=0} = props;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      paddingTop,
      paddingBottom
    }}>
      <div style={{
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        width: "100%"
      }}/>

      <span style={{
        padding: theme.spacing(1, 2)
      }}>
        <Chip
          style={{
            color: "rgba(0,0,0,0.6)"
          }}
          size="small"
          label={label}
        />
      </span>

      <div style={{
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        width: "100%"
      }}/>
    </div>
  );
};

interface IconToolBarProps {
  margin?: number;
  children: React.ReactNode;
}

export const IconToolBar = (props: IconToolBarProps) => {
  const {children, margin=0} = props;

  const classes = useStyles();

  return (
    <div className={classes.icons} style={{
      marginLeft: `${margin}em`,
      marginRight: `${margin}em`
    }}>
      {children}
    </div>
  );
};

export const ToolBarDivider = () => {
  const classes = useStyles();

  return (
    <Typography className={classes.verticalDivider}>{"|"}</Typography>
  );
};

interface FontAwesomeIconProps {
  icon: string;
  success?: boolean;
  error?: boolean;
  disabled?: boolean;
  listIcon?: boolean;
  color?: "inherit" | "disabled" | "success" | "error" | "primary" | "secondary" | "action" | "info" | "warning";
}

export const FontAwesomeIcon = (props: FontAwesomeIconProps) => {
  const {icon, color = "primary", success, error, disabled, listIcon} = props;

  const classes = useStyles();
  const errorColor = error ? classes.error : "";

  return (
    <Icon
      className={clsx(`fa fa-sm ${icon}`, classes.iconSize, {
        [classes.disabled]: disabled,
        [classes.listIcon]: listIcon
      })}
      color={color}
      classes={{
        colorPrimary: success ? classes.success : errorColor
      }}
    />
  );
};

interface ToolBarButtonProps {
  icon: string;
  title: string;
  onClick: (event?) => void;
  visible?: boolean;
  success?: boolean;
  error?: boolean;
  disabled?: boolean;
  color?: "inherit" | "disabled" | "success" | "error" | "primary" | "secondary" | "action" | "info" | "warning";
}

export const ToolBarButton = (props: ToolBarButtonProps) => {
  const {icon, visible=true, title, onClick, color, success,
    error, disabled, ...other} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const tooltipText = disabled
    ? `${title} (${t('toolbar.button-disabled')})`
    : title;

  return (
    <React.Fragment>
      {visible && (<ArrowTooltip title={tooltipText}>
        <Button
          size="small"
          className={classes.iconButton}
          onClick={disabled ? undefined : onClick}
          {...other}
        >
          <FontAwesomeIcon
            icon={icon}
            error={error}
            color={color}
            success={success}
            disabled={disabled}
          />
        </Button>
      </ArrowTooltip>)}
    </React.Fragment>
  );
};

interface SlimScrollbarProps {
  scrollHeight?: number;
  children: React.ReactNode;
}

export const SlimScrollbar = (props: SlimScrollbarProps) => {
  const {scrollHeight, children} = props;

  return (
    <React.Fragment>
      {/** Use scroll height that was passed in */}
      {scrollHeight && (<Scrollbars
        autoHide
        autoHeight
        autoHeightMin={scrollHeight}
        autoHeightMax={scrollHeight}
        autoHideTimeout={250}
        autoHideDuration={100}
      >
        {children}
      </Scrollbars>)}

      {/** No scroll height was provided */}
      {!scrollHeight && (<Scrollbars
        autoHide
        autoHideTimeout={250}
        autoHideDuration={100}
      >
        {children}
      </Scrollbars>)}
    </React.Fragment>
  );
};

interface DynamicScrollbarProps {
  scrollHeight: number;
  scrollPercent: number;
  children: React.ReactNode;
}

export const DynamicScrollbar = (props: DynamicScrollbarProps) => {
  const {scrollHeight, scrollPercent, children} = props;

  const [scroll, setScroll] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const listElementID = useState(nanoid())[0];

  const getMaxHeight = () => {
    const percentHeight = (scrollPercent/100) * window.innerHeight;
    return Math.min(scrollHeight, percentHeight);
  };

  const checkScrollHeight = () => {
    let element = document.getElementById(listElementID);
    if (!element) return;

    const height = element.offsetHeight;
    const maxHeight = getMaxHeight();

    setScroll(height > maxHeight);
    setMaxHeight(maxHeight);
  };

  useEffect(() => {
    checkScrollHeight();
    window.addEventListener('resize', checkScrollHeight);

    return () => {
      window.removeEventListener('resize', checkScrollHeight);
    };
  }, []);

  useEffect(() => {
    checkScrollHeight();
  }, [children, window.innerHeight]);

  return (
    <React.Fragment>
      {(!scroll) && (
        <div id={listElementID}>
          {children}
        </div>
      )}

      {(scroll) && (
        <SlimScrollbar scrollHeight={maxHeight}>
          <div id={listElementID}>
            {children}
          </div>
        </SlimScrollbar>
      )}
    </React.Fragment>
  );
};